import { useState } from 'react'
import { useFaultStudyCombined } from '../../hooks/useFaultAnalysis.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import type { NodeFaultCurrent } from '../../types/faultAnalysis.ts'
import { IndicativeBanner, INDICATIVE_VALUE_COLOR, SetupDataChip } from './indicative.tsx'
import { Term } from '../common/Term.tsx'
import { AssumptionsDisclosure } from './AssumptionsDisclosure.tsx'
import { friendlyError } from '../../utils/friendlyError.ts'

interface FaultAnalysisProps {
  feederId: number
}

type SortKey = 'distance_from_source_ft' | 'fault_current_3ph_ka' | 'fault_current_slg_ka' | 'x_r_ratio_at_fault'
type SortDir = 'asc' | 'desc'

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  )
}

function sortNodes(nodes: NodeFaultCurrent[], key: SortKey, dir: SortDir): NodeFaultCurrent[] {
  return [...nodes].sort((a, b) => {
    const av = a[key] ?? -Infinity
    const bv = b[key] ?? -Infinity
    return dir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0)
  })
}

export function FaultAnalysis({ feederId }: FaultAnalysisProps) {
  const { data, isLoading, error } = useFaultStudyCombined(feederId, r => r.study)
  const [sortKey, setSortKey] = useState<SortKey>('distance_from_source_ft')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  // X/R + asymmetric columns are engineer detail — hidden by default (B3).
  const [showEngCols, setShowEngCols] = useState(false)

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return ''
    return sortDir === 'asc' ? ' \u2191' : ' \u2193'
  }

  if (isLoading) {
    return <div className="text-sm text-gray-400 p-4">Running fault study...</div>
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 p-4">
        {friendlyError(error, 'load the fault study')}
      </div>
    )
  }

  if (!data) {
    return <div className="text-sm text-gray-400 p-4">No fault study data available</div>
  }

  const { summary, nodes } = data
  const sorted = sortNodes(nodes, sortKey, sortDir)
  // A1 demotion: grey stat values and fault-magnitude cells while indicative.
  const statColor = data.indicative_only ? INDICATIVE_VALUE_COLOR : '#374151'
  const muted = data.indicative_only ? ' indicative-muted' : ''

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Fault Current Profile</h3>
        <div className="flex items-center gap-3">
          {data.nominal_voltage_kv && (
            <span className="text-xs text-gray-500">{data.nominal_voltage_kv} kV nominal</span>
          )}
          {data.source_fault_current_ka && (
            <span className="text-xs text-gray-500">
              Source: {data.source_fault_current_ka.toFixed(2)} kA
            </span>
          )}
        </div>
      </div>
      <AsOfStamp computedAt={data.analysis_timestamp} />

      {data.indicative_only && <IndicativeBanner />}

      {/* Summary cards. The "Nodes > 8 kA" card is gone (D-1): interpretation
          comes from device ratings via the duty rollup at the top of the tab,
          not from a constant threshold. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard
          label="Max 3-Phase (kA)"
          value={summary.max_fault_3ph_ka.toFixed(2)}
          color={statColor}
        />
        <StatCard
          label="Min 3-Phase (kA)"
          value={summary.min_fault_3ph_ka.toFixed(2)}
          color={statColor}
        />
        <StatCard
          label="Max SLG (kA)"
          value={summary.max_fault_slg_ka !== null ? summary.max_fault_slg_ka.toFixed(2) : '\u2014'}
          color={statColor}
        />
        <StatCard
          label="Min SLG (kA)"
          value={summary.min_fault_slg_ka !== null ? summary.min_fault_slg_ka.toFixed(2) : '\u2014'}
          color={statColor}
        />
        <StatCard
          label="Total Nodes"
          value={summary.total_nodes}
          color={statColor}
        />
      </div>

      {/* Warnings render once per tab via WarningSummary (spec §3.1). */}

      {/* A3 (spec §3.5): the modelling assumptions this run actually used. */}
      <AssumptionsDisclosure assumptions={data.assumptions ?? []} />

      {/* Disclaimer */}
      <div className="text-xs text-gray-400 italic">
        Bolted fault -- motor contribution not included
      </div>

      {/* Fault current table */}
      {sorted.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Node Fault Currents ({sorted.length})
              {data.indicative_only && <SetupDataChip />}
            </h4>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showEngCols}
                onChange={e => {
                  const on = e.target.checked
                  setShowEngCols(on)
                  // Never leave the table sorted by a hidden column.
                  if (!on && sortKey === 'x_r_ratio_at_fault') {
                    setSortKey('distance_from_source_ft')
                    setSortDir('asc')
                  }
                }}
              />
              Engineering columns
            </label>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-1 pr-2">Node</th>
                  <th className="pb-1 pr-2 text-right">Voltage (kV)</th>
                  <th
                    className="pb-1 pr-2 text-right cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => handleSort('distance_from_source_ft')}
                  >
                    Distance (ft){sortIndicator('distance_from_source_ft')}
                  </th>
                  <th
                    className="pb-1 pr-2 text-right cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => handleSort('fault_current_3ph_ka')}
                  >
                    3-Ph (kA){sortIndicator('fault_current_3ph_ka')}
                  </th>
                  <th
                    className="pb-1 pr-2 text-right cursor-pointer hover:text-gray-800 select-none"
                    onClick={() => handleSort('fault_current_slg_ka')}
                  >
                    <Term k="slg">SLG (kA)</Term>{sortIndicator('fault_current_slg_ka')}
                  </th>
                  {showEngCols && (
                    <>
                      <th
                        className="pb-1 pr-2 text-right cursor-pointer hover:text-gray-800 select-none"
                        onClick={() => handleSort('x_r_ratio_at_fault')}
                      >
                        <Term k="xr">X/R</Term>{sortIndicator('x_r_ratio_at_fault')}
                      </th>
                      <th className="pb-1 pr-2 text-right"><Term k="asym">Asym Peak (kA)</Term></th>
                      <th className="pb-1 text-right"><Term k="asym">Asym RMS (kA)</Term></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {sorted.map(node => {
                  return (
                    <tr key={node.node_name} className="border-b border-gray-50">
                      <td className="py-1 pr-2 text-gray-700 font-medium">{node.node_name}</td>
                      <td className="py-1 pr-2 text-right text-gray-600">
                        {node.voltage_kv !== null ? node.voltage_kv.toFixed(2) : '\u2014'}
                      </td>
                      <td className="py-1 pr-2 text-right text-gray-600">
                        {node.distance_from_source_ft.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </td>
                      <td className={`py-1 pr-2 text-right font-medium text-gray-800${muted}`}>
                        {node.fault_current_3ph_ka.toFixed(2)}
                      </td>
                      <td className={`py-1 pr-2 text-right text-gray-600${muted}`}>
                        {node.fault_current_slg_ka !== null ? node.fault_current_slg_ka.toFixed(2) : '\u2014'}
                      </td>
                      {showEngCols && (
                        <>
                          <td className={`py-1 pr-2 text-right text-gray-600${muted}`}>
                            {node.x_r_ratio_at_fault !== null ? node.x_r_ratio_at_fault.toFixed(1) : '\u2014'}
                          </td>
                          <td className={`py-1 pr-2 text-right text-gray-600${muted}`}>
                            {node.asymmetric_peak_ka !== null ? node.asymmetric_peak_ka.toFixed(2) : '\u2014'}
                          </td>
                          <td className={`py-1 text-right text-gray-600${muted}`}>
                            {node.asymmetric_rms_ka !== null ? node.asymmetric_rms_ka.toFixed(2) : '\u2014'}
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sorted.length === 0 && (
        <div className="text-sm text-gray-400 p-2">No fault current nodes computed</div>
      )}
    </div>
  )
}
